import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import strings from "translate/language";
import {
  channelPartnerDetail,
  channelPartnerStatusUpdate,
} from "reduxStore/action/channelPartnerAction";
import { useDispatch, useSelector } from "react-redux";
import {
  getFileExtension,
  handleDetailResponse,
  isEmpty,
  seo,
} from "utils/utils";
import Breadcrumb from "component/common/breadcrumb";
import { userPasswordGenrate, forceLogoutUser } from "reduxStore/action/usersActions";
import usePermission from "utils/usePermission";
import moment from "moment";
import ImageRender from "component/common/ImageRender";
import ProfileAvatar from "component/common/ProfileAvatar";
import PdfViewer from "../../component/common/pdfViewer";
import JustoPasswordResetModal from "component/common/JustoPasswordResetModal";
import JustoForceLogoutModal from "component/common/JustoForceLogoutModal";
import "./ChannelPartnerListing.css";
import "component/common/JustoConfirmModal.css";

const displayValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return strings.notavailable;
  }
  return value;
};

const formatDateTime = (value) =>
  value ? moment(value).format("DD-MM-yyyy hh:mm a") : strings.notavailable;

const FactCell = ({ label, value, capitalize = false }) => (
  <div className="justo-cp-detail__fact">
    <span className="justo-cp-detail__fact-label">{label}</span>
    <span
      className={`justo-cp-detail__fact-value${
        capitalize ? " justo-cp-detail__fact-value--cap" : ""
      }`}
    >
      {value}
    </span>
  </div>
);

const FactSection = ({ title, children, className = "" }) => (
  <div className={`justo-cp-detail__section ${className}`.trim()}>
    <h2 className="justo-cp-detail__section-title">{title}</h2>
    <div className="justo-cp-detail__facts">{children}</div>
  </div>
);

const DocPreview = ({ url, baseUrl = "", label, onOpenPdf }) => {
  if (!url) return strings.notavailable;
  const isPdf = getFileExtension(url) === "pdf";
  if (isPdf) {
    return (
      <button
        type="button"
        className="justo-cp-detail__doc-btn"
        title={label || "View document"}
        onClick={() => onOpenPdf(baseUrl ? `${baseUrl}${url}` : url)}
      >
        <img
          src="/assets/images/documentIcon.png"
          className="justo-cp-detail__doc-icon"
          alt=""
        />
        <span>View PDF</span>
      </button>
    );
  }
  return (
    <div className="justo-cp-detail__doc-image">
      <ImageRender
        baseUrl={baseUrl || undefined}
        imageUrl={url}
        thumbnailUrl={url}
      />
    </div>
  );
};

const ViewChannelPartner = () => {
  const navigate = useNavigate();
  const [viewData, setViewData] = useState({});
  const [isPdfView, setIsPdfView] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [passwordResetSubmitting, setPasswordResetSubmitting] = useState(false);
  const [forceLogoutOpen, setForceLogoutOpen] = useState(false);
  const [forceLogoutSubmitting, setForceLogoutSubmitting] = useState(false);
  const dispatch = useDispatch();
  const params = useParams();
  const { _id = "" } = params || {};
  const { edit } = usePermission({
    edit: "edit_channelpartner",
  });

  const channelPartnerDetailSelector =
    useSelector((state) => state.channelPartner) || {};

  useEffect(() => {
    getDetail();
  }, []);

  const getDetail = () => {
    dispatch(channelPartnerDetail({ cp_id: _id }));
  };

  useEffect(() => {
    if (channelPartnerDetailSelector.detail) {
      const { response } = channelPartnerDetailSelector;
      if (response?.status === 200) {
        setViewData(handleDetailResponse(response.data));
      }
    }
  }, [channelPartnerDetailSelector]);

  useEffect(() => {
    seo({
      title: "Channel Partner Details",
      metaDescription: "Channel partner details",
      canonicalLink: window.location.href,
    });
  }, []);

  const handlePassword = async ({ send_email } = {}) => {
    if (isEmpty(viewData?.login_id)) {
      return;
    }
    setPasswordResetSubmitting(true);
    try {
      await dispatch(
        userPasswordGenrate({
          user_id: viewData.login_id,
          send_email: Boolean(send_email),
        })
      );
      setPasswordResetOpen(false);
    } finally {
      setPasswordResetSubmitting(false);
    }
  };

  const handleForceLogout = async () => {
    if (isEmpty(viewData?.login_id)) {
      return;
    }
    setForceLogoutSubmitting(true);
    try {
      await dispatch(forceLogoutUser({ user_id: viewData.login_id }));
      setForceLogoutOpen(false);
    } finally {
      setForceLogoutSubmitting(false);
    }
  };

  const handleCpVerify = (data) => {
    dispatch(channelPartnerStatusUpdate(data));
    getDetail();
  };

  const openPdf = (url) => {
    setPdfUrl(url);
    setIsPdfView(true);
  };

  const isIndividual = viewData?.cp_type === 1;
  const verifiedNum = Number(viewData?.verified);
  const isVerified = verifiedNum === 1;
  const isPendingVerify = verifiedNum === 2;
  const agentCount = viewData?.agent_stats?.total_agent || 0;

  const closingPercentage = viewData?.agent_stats?.total_closing_percentage
    ? Number(
        String(viewData.agent_stats.total_closing_percentage).replace("%", "")
      ).toFixed(2) + "%"
    : strings.notavailable;

  const genderLabel = viewData?.gender
    ? viewData.gender === 1
      ? "Male"
      : "Female"
    : strings.notavailable;

  const workingLocations =
    Array.isArray(viewData?.working_location) &&
    viewData.working_location.length > 0
      ? viewData.working_location
          .map((item) => item?.location)
          .filter(Boolean)
      : [];

  const pageTitle = isIndividual
    ? strings.channelPartnerDetail
    : strings.companyDetals;

  if (isPdfView) {
    return (
      <PdfViewer
        pdfUrl={pdfUrl}
        backButton={() => {
          setIsPdfView(false);
          setPdfUrl(null);
        }}
      />
    );
  }

  return (
    <div className="page-body justo-cp-page">
      <JustoPasswordResetModal
        open={passwordResetOpen}
        onClose={() =>
          !passwordResetSubmitting && setPasswordResetOpen(false)
        }
        onConfirm={handlePassword}
        submitting={passwordResetSubmitting}
        userName={
          viewData?.agent_name ||
          [viewData?.firstname, viewData?.lastname].filter(Boolean).join(" ") ||
          viewData?.company_name
        }
        email={viewData?.email}
        mobile={viewData?.primary_mobile || viewData?.mobile}
      />
      <JustoForceLogoutModal
        open={forceLogoutOpen}
        onClose={() => !forceLogoutSubmitting && setForceLogoutOpen(false)}
        onConfirm={handleForceLogout}
        submitting={forceLogoutSubmitting}
        userName={
          viewData?.agent_name ||
          [viewData?.firstname, viewData?.lastname].filter(Boolean).join(" ") ||
          viewData?.company_name
        }
        email={viewData?.email}
        mobile={viewData?.primary_mobile || viewData?.mobile}
      />

      <div
        className="modal fade"
        id="exampleModal12"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                {strings.verifyCP}
              </h5>
              <button
                className="close"
                type="button"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="text-center">
                <h6>{strings.areYouSure}</h6>
              </div>
              <div className="text-center">
                <h6>{strings.youWantVerifyCP}</h6>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                className="btn btn-success mr-5"
                type="button"
                onClick={() => handleCpVerify({ verified: 1, cp_id: _id })}
                data-dismiss="modal"
              >
                {strings.yes}
              </button>
              <button
                className="btn btn-primary"
                type="button"
                data-dismiss="modal"
              >
                {strings.no}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Breadcrumb
        title={strings.channelPartnerDetail}
        url={"/channelpartner"}
        parentListing={strings.channelPartnerListing}
        titleactive={strings.viewchannelpartner}
        isBackBtn={true}
      />

      <div className="container-fluid justo-cp">
        <div className="justo-cp__card justo-cp-detail">
          <div className="justo-cp-detail__hero">
            <div className="justo-cp-detail__hero-avatar">
              <ProfileAvatar
                variant="detail"
                baseUrl={viewData?.profile_base_url}
                profilePicture={viewData?.profile_picture}
                alt={viewData?.agent_name || pageTitle}
              />
            </div>
            <div className="justo-cp-detail__hero-main">
              <h1 className="justo-cp-detail__name">
                {displayValue(viewData?.agent_name)}
              </h1>
              <p className="justo-cp-detail__meta">
                <span>
                  {displayValue(
                    viewData?.role_title ||
                      (isIndividual ? "Channel Partner" : "Agency")
                  )}
                </span>
                <span>{displayValue(viewData?.primary_mobile)}</span>
                <span>{displayValue(viewData?.email)}</span>
              </p>
            </div>
            <div className="justo-cp-detail__hero-actions">
              <span
                className={`cp-verify-badge ${
                  isVerified
                    ? "cp-verify-badge--verified"
                    : isPendingVerify
                      ? "cp-verify-badge--pending"
                      : "cp-verify-badge--unverified"
                }`}
              >
                <i
                  className={`fa ${
                    isVerified
                      ? "fa-check-circle"
                      : isPendingVerify
                        ? "fa-clock-o"
                        : "fa-exclamation-circle"
                  }`}
                  aria-hidden="true"
                />
                <span>
                  {isVerified
                    ? "Verified"
                    : isPendingVerify
                      ? "Pending"
                      : "Unverified"}
                </span>
              </span>
              {isPendingVerify ? (
                <button
                  type="button"
                  className="justo-cp__btn justo-cp__btn--solid"
                  data-toggle="modal"
                  data-target="#exampleModal12"
                >
                  {strings.verify}
                </button>
              ) : null}
              {edit ? (
                <>
                  <button
                    type="button"
                    className="justo-cp__btn justo-cp__btn--ghost"
                    onClick={() => setForceLogoutOpen(true)}
                  >
                    {strings.forceLogoutUser}
                  </button>
                  <button
                    type="button"
                    className="justo-cp__btn justo-cp__btn--outline"
                    onClick={() => setPasswordResetOpen(true)}
                  >
                    {strings.userPasswordSend}
                  </button>
                </>
              ) : null}
              {agentCount > 0 ? (
                <button
                  type="button"
                  className="justo-cp__btn justo-cp__btn--ghost"
                  onClick={() =>
                    navigate(
                      "/cp_agent?channelpartner=" + viewData?.login_id
                    )
                  }
                >
                  {strings.cpAgentDetails}
                </button>
              ) : null}
            </div>
          </div>

          <div className="justo-cp-detail__stats">
            {viewData?.role_title === "Channel Partner" || isIndividual ? (
              <div className="justo-cp-detail__stat">
                <span className="justo-cp-detail__stat-label">
                  {strings.noOfAgent}
                </span>
                <span className="justo-cp-detail__stat-value">{agentCount}</span>
              </div>
            ) : null}
            <div className="justo-cp-detail__stat">
              <span className="justo-cp-detail__stat-label">
                {strings.noOfSiteVisit}
              </span>
              <span className="justo-cp-detail__stat-value">
                {viewData?.agent_stats?.total_site_visit || 0}
              </span>
            </div>
            <div className="justo-cp-detail__stat">
              <span className="justo-cp-detail__stat-label">
                {strings.noOfClosingLead}
              </span>
              <span className="justo-cp-detail__stat-value">
                {viewData?.agent_stats?.total_closing_lead || 0}
              </span>
            </div>
            <div className="justo-cp-detail__stat">
              <span className="justo-cp-detail__stat-label">
                {strings.closingPercentage}
              </span>
              <span className="justo-cp-detail__stat-value">
                {closingPercentage}
              </span>
            </div>
          </div>

          <div className="justo-cp-detail__content">
            <div className="justo-cp-detail__grid">
              <FactSection title={isIndividual ? "Profile" : "Company"}>
                <FactCell
                  label={
                    isIndividual ? strings.ownerName : strings.realStateCompany
                  }
                  value={displayValue(viewData?.agent_name)}
                  capitalize
                />
                <FactCell
                  label={
                    isIndividual ? strings.mobileNo : strings.companyMobile
                  }
                  value={displayValue(viewData?.primary_mobile)}
                />
                {isIndividual ? (
                  <FactCell
                    label={strings.whatsappNo}
                    value={displayValue(viewData?.whatsapp_number)}
                  />
                ) : null}
                <FactCell
                  label={
                    isIndividual ? strings.cpMailID : strings.companyMailID
                  }
                  value={displayValue(viewData?.email)}
                />
                {isIndividual ? (
                  <FactCell label={strings.gender} value={genderLabel} />
                ) : null}
                <FactCell
                  label={
                    isIndividual ? strings.address : strings.companyAddress
                  }
                  value={displayValue(viewData?.location)}
                  capitalize
                />
                <FactCell
                  label={isIndividual ? strings.gst : strings.companyGST}
                  value={displayValue(viewData?.gst)}
                />
                <FactCell
                  label={
                    isIndividual
                      ? strings.reraCertiNumber
                      : strings.reraRegistration
                  }
                  value={displayValue(viewData?.rera_certificate_no)}
                />
              </FactSection>

              <FactSection title="Ownership">
                <FactCell
                  label={strings.createBy}
                  value={displayValue(viewData?.create_by || "Self Registered")}
                />
                <FactCell
                  label={strings.activeBy}
                  value={displayValue(viewData?.active_by)}
                />
                <FactCell
                  label={strings.lastLogin}
                  value={formatDateTime(viewData?.agent_stats?.last_login)}
                />
                <FactCell
                  label={strings.lastLeadCreate}
                  value={formatDateTime(viewData?.agent_stats?.last_lead_crate)}
                />
                <FactCell
                  label={strings.lastSiteVisit}
                  value={formatDateTime(viewData?.agent_stats?.last_site_visit)}
                />
                <FactCell
                  label={strings.lastCloseVisit}
                  value={formatDateTime(
                    viewData?.agent_stats?.last_closing_lead
                  )}
                />
              </FactSection>

              <FactSection
                title={
                  isIndividual ? strings.cpBankDetail : strings.agencyBankDetail
                }
              >
                <FactCell
                  label={strings.bankName}
                  value={displayValue(viewData?.cp_bank_detail?.bank_name)}
                  capitalize
                />
                <FactCell
                  label={strings.branchName}
                  value={displayValue(viewData?.cp_bank_detail?.branch_name)}
                  capitalize
                />
                <FactCell
                  label={strings.accountNo}
                  value={displayValue(viewData?.cp_bank_detail?.account_no)}
                />
                <FactCell
                  label={strings.ifscCode}
                  value={
                    viewData?.cp_bank_detail?.ifsc_code
                      ? String(viewData.cp_bank_detail.ifsc_code).toUpperCase()
                      : strings.notavailable
                  }
                />
              </FactSection>
            </div>

            {isIndividual && workingLocations.length > 0 ? (
              <div className="justo-cp-detail__grid justo-cp-detail__grid--wide">
                <FactSection title={strings.workingLocation}>
                  <div className="justo-cp-detail__chips">
                    {workingLocations.map((loc, i) => (
                      <span key={`${loc}-${i}`} className="justo-cp-detail__chip">
                        {loc}
                      </span>
                    ))}
                  </div>
                </FactSection>
              </div>
            ) : null}

            <div className="justo-cp-detail__grid justo-cp-detail__grid--wide">
              <FactSection title="Documents">
                <FactCell
                  label={
                    isIndividual
                      ? strings.reraCertificate
                      : strings.companyReraCertificate
                  }
                  value={
                    <DocPreview
                      url={viewData?.rera_certificate}
                      label="RERA certificate"
                      onOpenPdf={openPdf}
                    />
                  }
                />
                <FactCell
                  label={
                    isIndividual
                      ? strings.propDeclarLetter
                      : strings.companyDeclarLetter
                  }
                  value={
                    <DocPreview
                      url={viewData?.propidership_declaration_letter}
                      label="Declaration letter"
                      onOpenPdf={openPdf}
                    />
                  }
                />
                <FactCell
                  label={strings.cancelCheaque}
                  value={
                    <DocPreview
                      url={viewData?.cp_bank_detail?.cancel_cheaque}
                      baseUrl={viewData?.base_url || ""}
                      label="Cancelled cheque"
                      onOpenPdf={openPdf}
                    />
                  }
                />
                {!isIndividual ? (
                  <FactCell
                    label={strings.pancard}
                    value={
                      <DocPreview
                        url={viewData?.pancard}
                        label="PAN card"
                        onOpenPdf={openPdf}
                      />
                    }
                  />
                ) : null}
              </FactSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewChannelPartner;
